import { ChangeEventHandler, memo, useCallback } from 'react';
import { useTranslation } from '@bonanza-social/i18n';
import { logError } from '@bonanza-social/toolkit/logging';
import { UploadImagesProps } from './UploadImages.types';
import { MEGABYTE_IN_BYTE, SUPPORTED_FORMATS } from './UploadImages.content';
import UploadedImages from './UploadedImages/UploadedImages';
import Box from '../Box/Box';
import Icon from '../Icon/Icon';
import Text from '../Text/Text';

function UploadImages({
  images,
  sizeLimit,
  quality,
  maxWidth,
  maxHeight,
  mediaType,
  formatAsMp4,
  includeExtra,
  videoQuality,
  includeBase64,
  selectionLimit,
  presentationStyle,
  assetRepresentationMode,
  onSizeLimitReached,
  onChange,
  ...rest
}: UploadImagesProps) {
  const { t } = useTranslation('common');
  const disabled = selectionLimit === 0;

  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      try {
        const assets = Array.from(event.target.files || []);

        if (!assets || !assets.length) {
          return;
        }

        const sizeLimitReached = Array.from(assets).some(
          ({ size }) => size > sizeLimit * MEGABYTE_IN_BYTE,
        );

        if (sizeLimitReached) {
          onSizeLimitReached?.(sizeLimit, assets);

          return;
        }

        onChange([...images, ...assets]);
      } catch (error) {
        logError('Upload Images Error', error);
      }
    },
    [images, sizeLimit, onChange, onSizeLimitReached],
  );

  const onDelete = useCallback(
    (index: number) => {
      onChange([...images.slice(0, index), ...images.slice(index + 1)]);
    },
    [images, onChange],
  );

  return (
    <Box gap="space-400" {...rest}>
      <Box
        padding="space-300"
        borderRadius="radius-200"
        borderWidth="border-width-050"
        borderColor="borderGoldSolid"
        borderStyle="dashed"
        pointerEvents={disabled ? 'none' : undefined}
        opacity={disabled ? 0.3 : undefined}
      >
        <Box flexDirection="row" paddingBottom="space-100" centerContent>
          <Icon
            name="fileUpload"
            gradient="accentBackground"
            width="space-600"
            height="space-600"
          />
          <Text
            variant="button-large"
            gradient="darkSurfaceAccent"
            locKey="upload"
            locNS="common"
          />
        </Box>
        <Text variant="caption" color="darkSurfacePrimary" textAlign="center">
          {t('imagesSupportedFormats', {
            formats: SUPPORTED_FORMATS.slice(0, -1).join(', '),
            lastFormat: SUPPORTED_FORMATS[SUPPORTED_FORMATS.length - 1],
            sizeLimit,
            sizeUnit: 'MB',
          })}
        </Text>
        <input
          type="file"
          disabled={disabled}
          multiple={selectionLimit !== undefined && selectionLimit > 1}
          style={{
            position: 'absolute',
            cursor: 'pointer',
            inset: 0,
            opacity: 0,
          }}
          onChange={handleChange}
        />
      </Box>
      <UploadedImages images={images} onDelete={onDelete} />
    </Box>
  );
}

export default memo(UploadImages);
